/// <amd-module name="Dashboards/ConfigHost/HostImplementations/LegacyConfigHost" />
import { isNameValid, NameField } from "Dashboards/CommonConfig/NameField";
import { SizePicker } from "Dashboards/CommonConfig/SizePicker";
import { ConfigProps, LoadedEventInfo } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { LegacyConfigCallbacks, LegacyConfigProps } from "Dashboards/WidgetContracts/Internal/LegacyConfigProps";
import * as React from "react";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

/** Contribution backed information about what is supported in the widget Configuration. */

export interface LegacyConfigHostProps extends ConfigProps, IVssComponentProperties {}

interface LegacyConfigHostState {
    isLoading: boolean;
}

/** The Legacy Config host behaves as a Config host, and mediates specialized asks required of Legacy. */
export class LegacyConfigHost extends VssComponent<LegacyConfigHostProps, LegacyConfigHostState> {
    constructor(props: LegacyConfigHostProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isLoading: true
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (!this.hasCustomConfig()) {
            this.onConfigLoaded();
        }
    }

    public render(): JSX.Element {
        return (
            <div className="legacy-config-host">
                <div className="legacy-config-common-fields">
                    <NameField
                        isNameConfigurable={this.props.contributionConstraints.isNameConfigurable}
                        value={this.props.config.name}
                        onChange={this.onNameChanged}
                    />

                    <SizePicker
                        key="size-picker"
                        onChange={this.onSizeChanged}
                        sizes={this.props.contributionConstraints.supportedSizes}
                        value={this.props.config.size}
                    />
                </div>
                {this.hasCustomConfig() && <LegacyConfigHostRenderer {...this.interpretPropsForRenderer()} />}
                {!this.hasCustomConfig() && <EmptyCustomConfig {...this.interpretPropsForRenderer()} />}
            </div>
        );
    }

    private hasCustomConfig(): boolean {
        return this.props.config.configurationContributionId != null;
    }

    private hasValidGeneralConfig(name: string): boolean {
        return isNameValid(name);
    }

    /** Callbacks  for supporting legacy Widgets */
    private getWidgetName = (): string => {
        return this.props.config.name;
    };
    private setWidgetName = (name: string): void => {
        this.onNameChanged(name);
    };
    private getWidgetSize = (): WidgetGridSize => {
        return this.props.config.size;
    };

    /** Contemporary event handlers */
    private onConfigLoaded = (loadedEventInfo?: LoadedEventInfo): void => {
        if (this.state.isLoading) {
            this.setState({ isLoading: false });

            // relay event up the stack.
            this.props.parentProps.onConfigLoaded(loadedEventInfo);
        }
    };

    /* Relay Name changes to parent. */
    private onNameChanged = (value: string): void => {
        const newConfig = { ...this.props.config, name: value };
        this.props.parentProps.onUpdate(newConfig);
    };

    /* Relay Size changes to parent. */
    private onSizeChanged = (value: WidgetGridSize): void => {
        const newConfig = { ...this.props.config, size: value };
        this.props.parentProps.onUpdate(newConfig);
    };

    /* Relay Custom Config changes to parent. */
    private onUpdate = (widget: DashboardWidgetProps): void => {
        const newConfig = { ...this.props.config, settings: widget.settings, settingsVersion: widget.settingsVersion };
        this.props.parentProps.onUpdate(newConfig);
    };

    /* Relay validation response to parent. */
    private onValidationResponse = (widget: DashboardWidgetProps, hasValidCustomConfig: boolean): void => {
        const newConfig = { ...this.props.config, settings: widget.settings, settingsVersion: widget.settingsVersion };
        this.props.parentProps.onValidationResponse(newConfig, hasValidCustomConfig && this.hasValidGeneralConfig(this.props.config.name));
    };

    private interpretPropsForRenderer(): LegacyConfigProps {
        return {
            ...this.props,
            legacyRuntimeProps: {
                getCurrentWidgetName: this.getWidgetName,
                setCurrentWidgetName: this.setWidgetName,
                getCurrentWidgetSize: this.getWidgetSize
            },
            parentProps: {
                onConfigLoaded: this.onConfigLoaded,
                onUpdate: this.onUpdate,
                onValidationResponse: this.onValidationResponse
            },
            registerCallbacks: (callbacks: LegacyConfigCallbacks) => {}
        };
    }
}

/** Provides an intermediate layer for preventing re-renders of children */
class LegacyConfigHostRenderer extends React.PureComponent<LegacyConfigProps> {
    public render(): JSX.Element {
        return (
            <LegacyComponent
                key={"legacy-config-host-adapter"}
                modules={["Dashboards/Components/LegacyConfigHostAdapter"]}
                wrappedType="LegacyConfigHostAdapter"
                wrappedProps={this.props}
            />
        );
    }
}

/** Acts on behalf of a custom config implementation by simply acknowledging all save requests as allowed from custom settings POV. */
class EmptyCustomConfig extends React.PureComponent<LegacyConfigProps> {
    /** React to requests for widget Validation, to satisfy pattern of validation on save */
    public componentDidUpdate(prevProps: Readonly<LegacyConfigProps>): void {
        if (prevProps.saveValidationTimeStamp !== this.props.saveValidationTimeStamp) {
            this.props.parentProps.onValidationResponse(this.props.config, true);
        }
    }

    public render(): JSX.Element | null {
        return null;
    }
}
